import { fetchPlans } from '@api/requests';
import { useConfig } from '@providers/ConfigRedirectProvider';
import {
  Dispatch,
  ReactNode,
  SetStateAction,
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';

import {
  DEFAULT_TRIAL_PLAN,
  FUTURE_CHILD_REPORT_PLAN_ID,
  HEALTH_SCAN_REPORT_PLAN_ID,
  IPaymentPlan,
  MATCH_REPORT_PLAN_ID,
  PROFILE_PRO_PLAN_ID,
} from '../../AppConfig';

/**
 * Processes payment plans for multi-currency support
 * @param plans - Array of payment plans to process
 * @param isMultiCurrency - Flag indicating if multi-currency mode is enabled
 * @returns Processed plans with appropriate currency and pricing values
 */
const processPlansForMultiCurrency = (
  plans: IPaymentPlan[],
  isMultiCurrency: boolean,
): IPaymentPlan[] => {
  // If multi-currency is disabled, only ensure currency defaults to 'USD'
  if (!isMultiCurrency) {
    return plans.map(plan => ({
      ...plan,
      currency: plan.currency || 'USD',
    }));
  }

  // Multi-currency mode: process each plan to use local pricing when available
  return plans.map(plan => {
    const processedPlan = { ...plan };

    // Handle main pricing: prioritize sum_local over sum
    if (plan.sum_local?.value !== undefined) {
      processedPlan.sum = plan.sum_local.value;
      processedPlan.currency = plan.sum_local.currency;
    } else {
      processedPlan.currency = plan.currency || 'USD';
    }

    // Handle trial pricing: prioritize trial_sum_local over trial_sum
    if (plan.trial_sum_local?.value !== undefined) {
      processedPlan.trial_sum = plan.trial_sum_local.value;
    } else if (plan.trial_sum !== undefined) {
      processedPlan.trial_sum = plan.trial_sum;
    }

    // console.log('processedPlan', processedPlan);
    return processedPlan;
  });
};

export const PaymentConfigContext = createContext<null | {
  selectedPlan: IPaymentPlan;
  setSelectedPlan: Dispatch<SetStateAction<IPaymentPlan>>;
  multiPlans: IPaymentPlan[];
  skipTrialPlan: IPaymentPlan | undefined;
  ultPackPlan: IPaymentPlan | undefined;
  ultPackSalePlan: IPaymentPlan | undefined;
  profileProPlan: IPaymentPlan | undefined;
  futureChildReportPlan: IPaymentPlan | undefined;
  profileProSalePlan: IPaymentPlan | undefined;
  profileProSale2Plan: IPaymentPlan | undefined;
  matchReportPlan: IPaymentPlan | undefined;
  matchReportSalePlan: IPaymentPlan | undefined;
  matchReportSale2Plan: IPaymentPlan | undefined;
  healthScanPlan: IPaymentPlan | undefined;
  webappOfferProfileProPlan: IPaymentPlan | undefined;
  webappOfferMatchReportPlan: IPaymentPlan | undefined;
  webappOfferFutureChildReportPlan: IPaymentPlan | undefined;
  webappOfferHealthScanPlan: IPaymentPlan | undefined;
  trialPlans: IPaymentPlan[] | undefined;
  showAdditionalOffersPrice: boolean;
  showSkipPopup: boolean;
  isMultiPlans: boolean;
  isMultiCurrency: boolean;
  canPayWithPaypal: boolean;
}>(null);

function PaymentConfigProvider({ children }: { children: ReactNode }) {
  const [fetchedPlans, setFetchedPlans] = useState<IPaymentPlan[]>([]);
  const config = useConfig();
  const configParams = config.params;

  const findPlanIdFromConfig = useCallback(
    (param: string) => {
      const planId = configParams?.[param];

      if (!planId) return null;

      return planId as string;
    },
    [configParams],
  );

  const getPlanIdsFromConfig = useCallback(
    (param: string): string[] => {
      const value = configParams?.[param];

      if (!value) return [];

      if (Array.isArray(value)) {
        return value.map(String);
      }

      if (typeof value === 'string') {
        return value
          .split(',')
          .map(v => v.trim())
          .filter(Boolean);
      }

      return [];
    },
    [configParams],
  );

  const showAdditionalOffersPrice =
    configParams?.additional_offer_dc === 'true';

  const showSkipPopup = configParams?.sure_popup === 'true';

  const mainPlanId = useMemo(() => {
    return findPlanIdFromConfig('plan') ?? DEFAULT_TRIAL_PLAN.id;
  }, [findPlanIdFromConfig]);

  const multiPlanIds = useMemo(
    () => getPlanIdsFromConfig('plans'),
    [getPlanIdsFromConfig],
  );

  // Check multiCurrency from query params and localStorage
  const queryMultiCurrency = configParams?.multiCurrency === 'true';
  const localStorageMultiCurrency =
    localStorage.getItem('multiCurrency') === 'true';

  // If query param exists but localStorage doesn't, save it to localStorage
  if (queryMultiCurrency && !localStorage.getItem('multiCurrency')) {
    localStorage.setItem('multiCurrency', 'true');
  }

  const isMultiCurrency = queryMultiCurrency || localStorageMultiCurrency;

  const skipTrialPlanId = useMemo(
    () => findPlanIdFromConfig('skip_trial_plan'),
    [findPlanIdFromConfig],
  );

  const ultPackPlanPlanId = useMemo(
    () => findPlanIdFromConfig('ult_pack'),
    [findPlanIdFromConfig],
  );

  const ultPackSalePlanId = useMemo(
    () => findPlanIdFromConfig('ult_pack_s'),
    [findPlanIdFromConfig],
  );

  const profileProPlanId = useMemo(
    () => findPlanIdFromConfig('profile_pro'),
    [findPlanIdFromConfig],
  );

  const futureChildReportPlanId = useMemo(
    () => findPlanIdFromConfig('future_child_report'),
    [findPlanIdFromConfig],
  );

  const profileProSalePlanId = useMemo(
    () => findPlanIdFromConfig('profile_pro_s'),
    [findPlanIdFromConfig],
  );

  const profileProSale2PlanId = useMemo(
    () => findPlanIdFromConfig('profile_pro_s2'),
    [findPlanIdFromConfig],
  );

  const matchReportPlanId = useMemo(
    () => findPlanIdFromConfig('match_report'),
    [findPlanIdFromConfig],
  );

  const matchReportSalePlanId = useMemo(
    () => findPlanIdFromConfig('match_report_s'),
    [findPlanIdFromConfig],
  );

  const matchReportSale2PlanId = useMemo(
    () => findPlanIdFromConfig('match_report_s2'),
    [findPlanIdFromConfig],
  );

  const healthScanPlanId = useMemo(
    () => findPlanIdFromConfig('health_scan'),
    [findPlanIdFromConfig],
  );

  const canPayWithPaypal = configParams?.paypal === 'true';

  const trialPlansToSelectIds = useMemo(() => {
    return getPlanIdsFromConfig('plans_s');
  }, [getPlanIdsFromConfig]);

  const [selectedPlan, setSelectedPlan] = useState(DEFAULT_TRIAL_PLAN);
  const [multiPlans, setMultiPlans] = useState<IPaymentPlan[]>([]);

  const isMultiPlans = useMemo(() => {
    return multiPlans.length > 0;
  }, [multiPlans]);

  const webappOfferIds = [
    PROFILE_PRO_PLAN_ID,
    MATCH_REPORT_PLAN_ID,
    HEALTH_SCAN_REPORT_PLAN_ID,
    FUTURE_CHILD_REPORT_PLAN_ID,
  ];

  useEffect(() => {
    const ids = [
      ...multiPlanIds,
      ...webappOfferIds,
      ...(mainPlanId ? [mainPlanId] : []),
      ...(skipTrialPlanId ? [skipTrialPlanId] : []),
      ...(ultPackPlanPlanId ? [ultPackPlanPlanId] : []),
      ...(ultPackSalePlanId ? [ultPackSalePlanId] : []),
      ...(profileProPlanId ? [profileProPlanId] : []),
      ...(futureChildReportPlanId ? [futureChildReportPlanId] : []),
      ...(profileProSalePlanId ? [profileProSalePlanId] : []),
      ...(profileProSale2PlanId ? [profileProSale2PlanId] : []),
      ...(matchReportPlanId ? [matchReportPlanId] : []),
      ...(matchReportSalePlanId ? [matchReportSalePlanId] : []),
      ...(matchReportSale2PlanId ? [matchReportSale2PlanId] : []),
      ...(healthScanPlanId ? [healthScanPlanId] : []),
      ...(trialPlansToSelectIds ? trialPlansToSelectIds : []),
    ];
    if (ids.length > 0) {
      fetchPlans(ids)
        .then(res => {
          // Process plans for multi-currency support before storing them
          const processedPlans = processPlansForMultiCurrency(
            res.data,
            isMultiCurrency,
          );
          setFetchedPlans(processedPlans);
        })
        .catch(console.error);
    }
  }, [isMultiCurrency]);

  useEffect(() => {
    if (fetchedPlans.length > 0) {
      if (multiPlanIds.length > 0) {
        let plans: IPaymentPlan[] = [];

        multiPlanIds.forEach(multi_plan_id => {
          const fetchedPlan = fetchedPlans.find(el => el.id === multi_plan_id);
          if (fetchedPlan) {
            plans.push(fetchedPlan);
          }
        });

        if (plans.length > 0) {
          setMultiPlans(plans);
          setSelectedPlan(plans[1] ?? plans[0]);

          return;
        }
      }

      setSelectedPlan(
        fetchedPlans.find(el => el.id === mainPlanId) ?? DEFAULT_TRIAL_PLAN,
      );
    }
  }, [fetchedPlans]);

  const skipTrialPlan = useMemo(
    () => fetchedPlans.find(el => el.id === skipTrialPlanId),
    [fetchedPlans],
  );
  const ultPackPlan = useMemo(
    () => fetchedPlans.find(el => el.id === ultPackPlanPlanId),
    [fetchedPlans],
  );
  const ultPackSalePlan = useMemo(
    () => fetchedPlans.find(el => el.id === ultPackSalePlanId),
    [fetchedPlans],
  );
  const profileProPlan = useMemo(
    () => fetchedPlans.find(el => el.id === profileProPlanId),
    [fetchedPlans],
  );
  const futureChildReportPlan = useMemo(
    () => fetchedPlans.find(el => el.id === futureChildReportPlanId),
    [fetchedPlans],
  );
  const profileProSalePlan = useMemo(
    () => fetchedPlans.find(el => el.id === profileProSalePlanId),
    [fetchedPlans],
  );
  const profileProSale2Plan = useMemo(
    () => fetchedPlans.find(el => el.id === profileProSale2PlanId),
    [fetchedPlans],
  );
  const matchReportPlan = useMemo(
    () => fetchedPlans.find(el => el.id === matchReportPlanId),
    [fetchedPlans],
  );
  const matchReportSalePlan = useMemo(
    () => fetchedPlans.find(el => el.id === matchReportSalePlanId),
    [fetchedPlans],
  );
  const matchReportSale2Plan = useMemo(
    () => fetchedPlans.find(el => el.id === matchReportSale2PlanId),
    [fetchedPlans],
  );
  const healthScanPlan = useMemo(
    () => fetchedPlans.find(el => el.id === healthScanPlanId),
    [fetchedPlans],
  );
  const webappOfferProfileProPlan = useMemo(
    () => fetchedPlans.find(el => el.id === PROFILE_PRO_PLAN_ID),
    [fetchedPlans],
  );
  const webappOfferMatchReportPlan = useMemo(
    () => fetchedPlans.find(el => el.id === MATCH_REPORT_PLAN_ID),
    [fetchedPlans],
  );
  const webappOfferFutureChildReportPlan = useMemo(
    () => fetchedPlans.find(el => el.id === FUTURE_CHILD_REPORT_PLAN_ID),
    [fetchedPlans],
  );
  const webappOfferHealthScanPlan = useMemo(
    () => fetchedPlans.find(el => el.id === HEALTH_SCAN_REPORT_PLAN_ID),
    [fetchedPlans],
  );

  const trialPlans = useMemo(() => {
    return fetchedPlans.filter(plan => trialPlansToSelectIds.includes(plan.id));
  }, [fetchedPlans]);

  const value = {
    selectedPlan,
    setSelectedPlan,
    showAdditionalOffersPrice,
    ultPackPlan,
    ultPackSalePlan,
    profileProPlan,
    futureChildReportPlan,
    profileProSalePlan,
    profileProSale2Plan,
    matchReportPlan,
    matchReportSalePlan,
    matchReportSale2Plan,
    webappOfferProfileProPlan,
    webappOfferMatchReportPlan,
    webappOfferFutureChildReportPlan,
    webappOfferHealthScanPlan,
    trialPlans,
    skipTrialPlan,
    showSkipPopup,
    healthScanPlan,
    isMultiPlans,
    multiPlans,
    isMultiCurrency,
    canPayWithPaypal,
  };

  return (
    <PaymentConfigContext.Provider value={value}>
      {children}
    </PaymentConfigContext.Provider>
  );
}

export default PaymentConfigProvider;
